import { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useInvoicingScript } from 'helpers/invoicingWebComponent';
import { getCurrency, getLanguage, getTimezone } from 'helpers/locale';
import { getLocationId, getRegisterId } from 'helpers/session';
import { fetchSavedCards, PAYMENT_METHOD_TYPES } from 'root/apps/payments/SavedCardsAPI';
import LocationService from 'root/apps/QuoteMachineIntegration/services/LocationService';
import PaymentGatewayService from 'root/apps/QuoteMachineIntegration/services/PaymentGatewayService';
import DepositPaymentSaleService, {
  DepositPaymentSaleResult,
} from 'root/apps/QuoteMachineIntegration/services/v1/DepositPaymentSaleService';
import { INVOICING_INTEGRATION_KEY } from 'root/apps/QuoteMachineIntegration/utils/referenceHelpers';
import { FormattedAPISavedCard } from 'root/apps/SavedCards/SavedCards.types';
import { datadogRum } from 'root/datadog/datadogRum';

import { getPayBalanceScriptUrl, PAY_BALANCE_CUSTOM_ELEMENT } from './config';
import {
  InvoicingPayBalanceElement,
  PayBalanceDepositsSelectedDetail,
  PayBalanceLocationSelectedDetail,
  PayBalancePaymentCompletedDetail,
  PayBalancePaymentMethodSelectedDetail,
  PayBalanceStepPendingAckDetail,
} from './types';
import { toPayBalanceShops } from './utils';

const DEPOSIT_SELECT_STEP = 'deposit-select';

const EVENT_LOCATION_SELECTED = 'invoicing:pay-balance-location-selected';
const EVENT_PAYMENT_METHOD_SELECTED = 'invoicing:pay-balance-payment-method-selected';
const EVENT_DEPOSITS_SELECTED = 'invoicing:pay-balance-deposits-selected';
const EVENT_PAYMENT_COMPLETED = 'invoicing:pay-balance-payment-completed';
const EVENT_STEP_PENDING_ACK = 'invoicing:pay-balance-step-pending-ack';

interface UsePayBalanceProps {
  accountID: string;
  customerID: string;
  creditAccountID: number;
  customerName: string;
  customerEmail: string;
}

interface UsePayBalanceResult {
  mountPayBalance: (container: HTMLElement) => void;
  unmountPayBalance: (container: HTMLElement) => void;
}

export function usePayBalance({
  accountID,
  customerID,
  creditAccountID,
  customerName,
  customerEmail,
}: UsePayBalanceProps): UsePayBalanceResult {
  const queryClient = useQueryClient();
  const isScriptReady = useInvoicingScript(getPayBalanceScriptUrl(), PAY_BALANCE_CUSTOM_ELEMENT);
  const [isDataReady, setIsDataReady] = useState(false);
  const savedPaymentMethodsRef = useRef<FormattedAPISavedCard[]>([]);
  const shopsRef = useRef<Shop[]>([]);
  const lsPayEnabledShopIdsRef = useRef<Set<string>>(new Set());
  const shopIdRef = useRef<string>('');
  const registerIdRef = useRef<string>('');
  const depositResultRef = useRef<Promise<DepositPaymentSaleResult> | null>(null);
  const mountedRef = useRef<{
    el: InvoicingPayBalanceElement;
    handlers: Array<[string, EventListener]>;
  } | null>(null);

  useEffect(() => {
    Promise.all([
      fetchSavedCards(customerID, [
        PAYMENT_METHOD_TYPES.CARD,
        PAYMENT_METHOD_TYPES.US_BANK_ACCOUNT,
      ]).catch(() => [] as FormattedAPISavedCard[]),
      LocationService.getShopsWithRegisters().catch(() => [] as Shop[]),
      PaymentGatewayService.getLsPayEnabledShopIds().catch(() => new Set<string>()),
    ]).then(([savedCards, shops, lsPayEnabledShopIds]) => {
      savedPaymentMethodsRef.current = savedCards;
      shopsRef.current = shops;
      lsPayEnabledShopIdsRef.current = lsPayEnabledShopIds;
      setIsDataReady(true);
    });
  }, [customerID]);

  const mountPayBalance = useCallback(
    (container: HTMLElement) => {
      if (!isScriptReady || !isDataReady) return;
      const el = document.createElement(PAY_BALANCE_CUSTOM_ELEMENT) as InvoicingPayBalanceElement;

      shopIdRef.current = getLocationId();

      const handleLocationSelected = (event: Event) => {
        const detail = (event as CustomEvent<PayBalanceLocationSelectedDetail>).detail;
        shopIdRef.current = detail.shopId;
      };

      const handlePaymentMethodSelected = (event: Event) => {
        const detail = (event as CustomEvent<PayBalancePaymentMethodSelectedDetail>).detail;
        registerIdRef.current = detail.registerId ?? getRegisterId();
      };

      const handleDepositsSelected = (event: Event) => {
        const detail = (event as CustomEvent<PayBalanceDepositsSelectedDetail>).detail;

        if (!shopIdRef.current || !registerIdRef.current) {
          const missing = [
            !shopIdRef.current && 'shopId',
            !registerIdRef.current && 'registerId',
          ].filter(Boolean);
          const err = new Error(`Missing required fields: ${missing.join(', ')}`);
          datadogRum.addError(err, {
            message: 'Deposit payment aborted: location or payment method not selected',
            accountId: accountID,
          });
          depositResultRef.current = Promise.resolve({ success: false, error: err });
          return;
        }

        depositResultRef.current = DepositPaymentSaleService.createAndCompleteSaleWithDeposits({
          accountId: accountID,
          customerId: detail.customerId,
          creditAccountId: detail.creditAccountId,
          currency: detail.currency,
          shopId: shopIdRef.current,
          registerId: registerIdRef.current,
          depositAllocations: detail.depositAllocations,
          invoiceAllocations: detail.invoiceAllocations,
        });
      };

      const handleStepPendingAck = async (event: Event) => {
        const detail = (event as CustomEvent<PayBalanceStepPendingAckDetail>).detail;
        if (detail.step !== DEPOSIT_SELECT_STEP) return;

        const pending = depositResultRef.current;
        if (!pending) {
          el.update?.({
            stepAcknowledgement: {
              step: DEPOSIT_SELECT_STEP,
              result: 'fail',
              error: { message: 'Deposit payment did not start' },
            },
          });
          return;
        }

        const result = await pending;
        el.update?.({
          stepAcknowledgement: {
            step: DEPOSIT_SELECT_STEP,
            result: result.success ? 'success' : 'fail',
            ...(result.success
              ? {}
              : { error: { message: result.error?.message ?? 'Deposit payment failed' } }),
          },
        });
      };

      const handlePaymentCompleted = (event: Event) => {
        const { creditAccountId, savedPaymentMethodAdded } = (
          event as CustomEvent<PayBalancePaymentCompletedDetail>
        ).detail;
        if (creditAccountId !== creditAccountID.toString()) return;
        queryClient.invalidateQueries({ queryKey: ['creditAccountBalance', creditAccountId] });
        if (savedPaymentMethodAdded) {
          fetchSavedCards(customerID, [
            PAYMENT_METHOD_TYPES.CARD,
            PAYMENT_METHOD_TYPES.US_BANK_ACCOUNT,
          ])
            .then((cards) => {
              savedPaymentMethodsRef.current = cards;
            })
            .catch(() => {});
        }
      };

      const handlers: Array<[string, EventListener]> = [
        [EVENT_LOCATION_SELECTED, handleLocationSelected],
        [EVENT_PAYMENT_METHOD_SELECTED, handlePaymentMethodSelected],
        [EVENT_DEPOSITS_SELECTED, handleDepositsSelected],
        [EVENT_PAYMENT_COMPLETED, handlePaymentCompleted],
        [EVENT_STEP_PENDING_ACK, handleStepPendingAck],
      ];
      handlers.forEach(([name, handler]) => el.addEventListener(name, handler));
      mountedRef.current = { el, handlers };

      container.appendChild(el);
      el.init?.({
        baseUrl: `${window.location.origin}/admin/invoicing`,
        locale: getLanguage(),
        timezone: getTimezone(),
        currency: getCurrency().currencyCode,
        integrationKey: INVOICING_INTEGRATION_KEY,
        accountId: accountID,
        customerId: `${customerID}`,
        creditAccountId: creditAccountID.toString(),
        shops: toPayBalanceShops(shopsRef.current, lsPayEnabledShopIdsRef.current),
        savedPaymentMethods: savedPaymentMethodsRef.current,
        hasStoredCardPermission: window.merchantos.session.hasRight(
          'register_use_stored_card_on_file'
        ),
        merchantName: window.merchantos.session.getSystemCustomerName() ?? '',
        customerName,
        customerEmail,
        employeeId: window.merchantos.session.getEmployee()?.employeeID?.toString(),
        gatedSteps: [DEPOSIT_SELECT_STEP],
      }).catch(() => {});
    },
    [
      isScriptReady,
      isDataReady,
      accountID,
      customerID,
      creditAccountID,
      customerName,
      customerEmail,
      queryClient,
    ]
  );

  const unmountPayBalance = useCallback((container: HTMLElement) => {
    const mounted = mountedRef.current;
    if (mounted) {
      mounted.handlers.forEach(([name, handler]) => mounted.el.removeEventListener(name, handler));
      mountedRef.current = null;
    }
    depositResultRef.current = null;
    shopIdRef.current = '';
    registerIdRef.current = '';
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
  }, []);

  return { mountPayBalance, unmountPayBalance };
}
